import { Box, InputAdornment, MenuItem, Select, TextField } from '@mui/material';
import { useEffect, useRef } from 'react';
import type { CountryIso2 } from 'react-international-phone';
import {
  defaultCountries,
  FlagImage,
  parseCountry,
  usePhoneInput,
} from 'react-international-phone';

interface PhoneInputProps {
  label: string;
  value: string;
  onChange: (phone: string) => void;
  error?: boolean;
  helperText?: string;
  sx?: object;
}

// Shared international phone input (country selector + flag) so every entry point
// that creates/edits a Supplier saves `phone` in the same format.
export function PhoneInput({ label, value, onChange, error, helperText, sx }: PhoneInputProps) {
  const { inputValue, handlePhoneValueChange, inputRef, country, setCountry } = usePhoneInput({
    defaultCountry: 'it',
    value: value || '',
    countries: defaultCountries,
    disableDialCodeAndPrefix: true,
    onChange: function onPhoneChange(data) {
      onChange(data.phone);
    },
  });

  const hasInitialized = useRef(false);

  useEffect(
    function ensureDefaultCountry() {
      if (!hasInitialized.current && !value && !country?.iso2) {
        setCountry('it');
        hasInitialized.current = true;
      }
    },
    [value, country, setCountry],
  );

  const countryIso2: CountryIso2 = (country?.iso2 as CountryIso2) || 'it';
  const currentCountryData = parseCountry(
    defaultCountries.find(function findCountry(c) {
      const parsed = parseCountry(c);
      return parsed.iso2 === countryIso2;
    }) ?? defaultCountries[0],
  );

  return (
    <TextField
      label={label}
      value={inputValue}
      onChange={handlePhoneValueChange}
      inputRef={inputRef}
      autoComplete="off"
      fullWidth
      error={error}
      helperText={helperText}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <Select
                value={countryIso2}
                onChange={function handleCountryChange(event) {
                  setCountry(event.target.value as CountryIso2);
                }}
                renderValue={function renderCountryValue() {
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Box
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          '& img': {
                            width: '20px',
                            height: '15px',
                            objectFit: 'contain',
                          },
                        }}
                      >
                        <FlagImage iso2={currentCountryData.iso2} />
                      </Box>
                      <Box component="span" sx={{ fontSize: '0.875rem' }}>
                        +{currentCountryData.dialCode}
                      </Box>
                    </Box>
                  );
                }}
                sx={{
                  border: 'none',
                  minWidth: '100px',
                  '& .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    border: 'none',
                  },
                  '& .MuiSelect-select': {
                    padding: '0 8px',
                    display: 'flex',
                    alignItems: 'center',
                  },
                }}
              >
                {defaultCountries.map(function mapCountry(c) {
                  const countryData = parseCountry(c);
                  return (
                    <MenuItem key={countryData.iso2} value={countryData.iso2}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            '& img': {
                              width: '20px',
                              height: '15px',
                              objectFit: 'contain',
                            },
                          }}
                        >
                          <FlagImage iso2={countryData.iso2} />
                        </Box>
                        <Box component="span">{countryData.name}</Box>
                        <Box component="span" sx={{ ml: 'auto', color: 'text.secondary' }}>
                          +{countryData.dialCode}
                        </Box>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </InputAdornment>
          ),
        },
      }}
      sx={sx}
    />
  );
}
